import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import {
  isAccessDeniedError,
  PRACTICE_TYPE_ICONS,
  PRACTICE_TYPE_LABELS,
  type Practice as PracticeModel,
  type PracticeType,
} from '../api/types';
import { StreakDisplay } from '../components/StreakDisplay';
import { useUser } from '../state/UserContext';
import { mascotImagePath, TRADITION_LABELS } from '../theme/tokens';

const ACCESS_DENIED_MESSAGES: Record<string, string> = {
  // Names the actual accepted price (Work Package 4's "clear access reasons... $9.99 monthly
  // terms") without implying a working checkout -- billing/entitlements (Work Package 5) isn't
  // built yet, so this is informational only, never a "Subscribe" button that would do nothing.
  premium_required: 'This level requires Premium ($9.99/month).',
  prerequisite_required: 'Complete the previous level first.',
};

const GUIDE_VERBS: Record<PracticeType, string> = {
  reading: 'reads',
  prayer: 'prays',
  meditation: 'guides you',
  service: 'invites you to',
  movement: 'guides you through',
};

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function Practice() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { me, applySessionResult } = useUser();

  const [practice, setPractice] = useState<PracticeModel | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [justCompleted, setJustCompleted] = useState<{ currentStreak: number; longestStreak: number } | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPractice(null);

    // The practice's own content (title/script/description) and its narration audio are
    // deliberately fetched independently: narration synthesis (Polly, on a cache miss) is a
    // separate failure domain from "does this practice exist", and a synthesis hiccup should
    // degrade to "no audio available" rather than making the whole practice unreachable.
    api
      .getPractices({})
      .then((practicesRes) => {
        if (cancelled) return;
        const found = practicesRes.practices.find((p) => p.practiceId === id) ?? null;
        setPractice(found);
        setRemainingSeconds(found?.durationSeconds ?? 0);
        setLoading(false);
        // A locked level (reached via a direct link, an old bookmark, or a stale client cache)
        // renders its own "requires Premium"/"complete the previous level" state below instead --
        // the server would reject the audio fetch anyway (see voice.py's entitlement gate), so
        // there's no reason to even attempt it.
        if (!found || found.access !== 'available') return;

        api
          .getPracticeAudio(id)
          .then((audioRes) => {
            if (!cancelled) setAudioUrl(audioRes.url);
          })
          .catch((e) => {
            if (!cancelled) {
              setError(e instanceof Error ? `Narration unavailable: ${e.message}` : 'Narration is unavailable right now.');
            }
          });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load this practice.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Timer tick
  useEffect(() => {
    if (!isRunning) return;

    intervalRef.current = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          setIsRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [isRunning]);

  // Keep audio in sync with the timer's running state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isRunning) {
      audio.play().catch(() => {
        // Autoplay can be blocked until a user gesture; the Start button click itself is one,
        // so this mostly guards against edge cases.
      });
    } else {
      audio.pause();
    }
  }, [isRunning]);

  const handleStart = () => setIsRunning(true);
  const handlePause = () => setIsRunning(false);
  const handleReset = () => {
    setIsRunning(false);
    setRemainingSeconds(practice?.durationSeconds ?? 0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleMarkComplete = async () => {
    if (!practice) return;
    setCompleting(true);
    setError(null);
    setIsRunning(false);
    try {
      const elapsed = practice.durationSeconds - remainingSeconds;
      const result = await api.postSession({
        practiceId: practice.practiceId,
        durationSeconds: elapsed > 0 ? elapsed : practice.durationSeconds,
      });
      applySessionResult(result);
      setJustCompleted(result);
    } catch (e) {
      if (isAccessDeniedError(e) && e.access && ACCESS_DENIED_MESSAGES[e.access]) {
        setError(ACCESS_DENIED_MESSAGES[e.access]);
      } else {
        setError(e instanceof Error ? e.message : 'Could not save this session.');
      }
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="center-page" style={{ minHeight: '40vh' }}>
        <div className="spinner" />
        <p className="page-subtitle">Preparing your practice...</p>
      </div>
    );
  }

  if (!practice) {
    return (
      <div className="empty-state">
        <p>{error ?? 'We couldn’t find that practice.'}</p>
        <Link to="/home" className="link-back">&larr; Back to Home</Link>
      </div>
    );
  }

  if (practice.access !== 'available') {
    // Reached via a direct link, an old bookmark, or a stale client cache -- the server would
    // reject both the audio fetch and session completion for this practice (see
    // app.domain.resolve_practice_access), so this state is shown instead of a broken player.
    return (
      <div className="empty-state">
        <h1 className="page-title">{practice.title}</h1>
        <p>{ACCESS_DENIED_MESSAGES[practice.access] ?? 'This level isn’t available yet.'}</p>
        <Link to="/practices" className="link-back">&larr; Back to Practices</Link>
      </div>
    );
  }

  const progressPct = practice.durationSeconds > 0
    ? Math.round(((practice.durationSeconds - remainingSeconds) / practice.durationSeconds) * 100)
    : 0;

  return (
    <div>
      <div className="back-row">
        <Link to="/home" className="link-back">&larr; Back</Link>
      </div>

      <h1 className="page-title">{practice.title}</h1>
      <p className="page-subtitle">
        {TRADITION_LABELS[practice.tradition] ?? practice.tradition} &middot;{' '}
        {PRACTICE_TYPE_ICONS[practice.type]} {PRACTICE_TYPE_LABELS[practice.type]}
      </p>

      <div className="mascot-says">
        <img src={mascotImagePath(practice.tradition, me?.gender)} alt="" className="mascot-says-avatar" />
        <div className="mascot-says-bubble">
          <span className="mascot-says-label">Your guide {GUIDE_VERBS[practice.type]}</span>
          <p className="mascot-says-text">{practice.description}</p>
        </div>
      </div>

      {audioUrl && (
        <div className="audio-block">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio ref={audioRef} src={audioUrl} loop preload="auto" controls />
        </div>
      )}

      <div className="timer-wrap">
        <div className="timer-circle" style={{ '--progress': progressPct } as CSSProperties}>
          <div className="timer-circle-inner">
            <div className="timer-time">{formatTime(remainingSeconds)}</div>
            <div className="timer-label">{isRunning ? 'In progress' : remainingSeconds === 0 ? 'Complete' : 'Paused'}</div>
          </div>
        </div>

        <div className="timer-controls">
          {!isRunning ? (
            <button type="button" className="btn btn-primary" onClick={handleStart} disabled={remainingSeconds === 0}>
              {remainingSeconds === practice.durationSeconds ? 'Start' : 'Resume'}
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={handlePause}>Pause</button>
          )}
          <button type="button" className="btn btn-ghost" onClick={handleReset}>Reset</button>
        </div>
      </div>

      {practice.transcript && (
        <div className="transcript-panel">
          <button
            type="button"
            className="transcript-toggle"
            onClick={() => setShowTranscript((v) => !v)}
            aria-expanded={showTranscript}
          >
            {showTranscript ? 'Hide transcript' : 'Show transcript'}
          </button>
          {showTranscript && <p className="transcript-text">{practice.transcript}</p>}
        </div>
      )}

      {error && <p className="error-text">{error}</p>}

      {justCompleted ? (
        <div className="themed-card">
          <p className="session-complete-text">Session complete. Well done.</p>
          <StreakDisplay currentStreak={justCompleted.currentStreak} longestStreak={justCompleted.longestStreak} />
          <button type="button" className="btn btn-primary btn-block" onClick={() => navigate('/home')}>
            Back to Home
          </button>
        </div>
      ) : (
        <button type="button" className="btn btn-primary btn-block" onClick={handleMarkComplete} disabled={completing}>
          {completing ? 'Saving...' : 'Mark complete'}
        </button>
      )}

      {me && !justCompleted && (
        <p className="page-subtitle" style={{ marginTop: 16 }}>
          Current streak: {me.currentStreak} day{me.currentStreak === 1 ? '' : 's'}
        </p>
      )}

      <div className="source-card">
        <p className="source-card-status">
          {practice.reviewerStatus === 'reviewed' && practice.reviewerName
            ? `Reviewed by ${practice.reviewerName}`
            : 'Draft — not yet reviewed by a subject-matter expert'}
        </p>
        {practice.sourceRefs.length > 0 && (
          <p className="source-card-refs">Sources: {practice.sourceRefs.join(', ')}</p>
        )}
        {practice.guidanceRequired && (
          <p className="source-card-guidance">Best practiced with guidance from a teacher familiar with this tradition.</p>
        )}
      </div>
    </div>
  );
}
